import React, { useEffect, useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import { useAuth } from "@/lib/auth";
import { useSocial } from "@/lib/social";
import { useStreaming } from "@/lib/streaming";
import { TIDS } from "@/constants/testIds";
import { api } from "@/lib/api";
import { Radio, Trophy, LayoutDashboard, LogIn, UserPlus, Sparkles, Dices, Coins, Crown, MessageSquare, Users, ShoppingBag, Award } from "lucide-react";
import { LogoMark } from "@/components/Logo";

/**
 * Persistent "Now streaming from…" strip that surfaces above the top nav
 * while the current user is broadcasting. Shows a live pulse dot, viewer
 * count, and a jump-back link so the streamer never loses awareness of
 * their broadcast no matter what page they're on.
 */
function NowStreamingStrip() {
  const streaming = useStreaming();
  if (!streaming.isLive || !streaming.channelUsername) return null;
  return (
    <div
      className="w-full bg-gradient-to-r from-[color:var(--sc-pink)]/25 via-[color:var(--sc-pink)]/15 to-transparent border-b border-[color:var(--sc-pink)]/40"
      data-testid="now-streaming-strip"
    >
      <div className="max-w-[1600px] mx-auto flex items-center gap-4 px-4 lg:px-8 py-1.5 text-[11px]">
        <div className="flex items-center gap-2">
          <span
            className="rec-dot anim-pulse"
            style={{ background: "#FF3366", boxShadow: "0 0 8px #FF3366", width: 8, height: 8 }}
            data-testid="now-streaming-pulse"
          />
          <span data-testid="now-streaming-label" className="sc-mono uppercase tracking-widest font-black text-[color:var(--sc-pink)]">Live now</span>
        </div>
        <span className="text-zinc-300 truncate">
          Streaming from <span data-testid="now-streaming-username" className="font-bold text-white">@{streaming.channelUsername}</span>
        </span>
        <div className="flex-1" />
        <div className="hidden sm:flex items-center gap-1.5 sc-mono text-zinc-300" data-testid="now-streaming-viewers">
          <Users className="w-3 h-3" />
          <span><span className="font-bold text-white">{streaming.viewerCount || 0}</span> watching</span>
        </div>
        <Link
          to={`/c/${streaming.channelUsername}/live`}
          data-testid="now-streaming-goto"
          className="text-[10px] sc-mono uppercase tracking-widest font-bold text-[color:var(--sc-cyan)] hover:text-white transition"
        >
          Return →
        </Link>
      </div>
    </div>
  );
}

export default function Navbar() {
  const { user, logout } = useAuth();
  const social = useSocial();
  const nav = useNavigate();
  const [liveCount, setLiveCount] = useState(0);

  useEffect(() => {
    let alive = true;
    const load = () => api.get("/community/pulse").then(r => { if (alive) setLiveCount(r.data?.live_channels || 0); }).catch(() => {});
    load();
    const id = setInterval(load, 30000);
    return () => { alive = false; clearInterval(id); };
  }, []);

  return (
    <header className="sticky top-0 z-40 glass border-b border-white/10">
      <NowStreamingStrip />
      <div className="flex items-center justify-between px-6 lg:px-10 h-16">
        <Link to="/" className="flex items-center gap-2.5 group" data-testid="nav-logo">
          <LogoMark size={38} />
          <span className="sc-heading text-xl font-black tracking-tighter">
            <span style={{ color: "var(--sc-pink)" }} className="text-glow-pink">MYSTI</span><span style={{ color: "var(--sc-volt)", textShadow: "0 0 12px var(--sc-volt)" }}>CAST</span>
          </span>
        </Link>

        <nav className="hidden md:flex items-center gap-1">
          {[
            { to: "/browse", icon: Radio, label: "Browse Live Streams", testId: TIDS.navBrowse },
            { to: "/games", icon: Dices, label: "Arcade", testId: "nav-games" },
            { to: "/lfg", icon: Users, label: "LFG", testId: "nav-lfg" },
            { to: "/shop", icon: ShoppingBag, label: "Shop", testId: "nav-shop" },
            { to: "/tournaments", icon: Trophy, label: "Tournaments", testId: TIDS.navTournaments },
            { to: "/leaderboard", icon: Crown, label: "Leaderboard", testId: "nav-leaderboard" },
            { to: "/account/plans", icon: Award, label: "Plans", testId: "nav-plans" },
          ].map(n => (
            <Link
              key={n.to}
              data-testid={n.testId}
              to={n.to}
              className="relative px-3 py-2 text-xs uppercase tracking-widest font-bold text-white hover:text-white/80 transition flex items-center gap-1.5 rounded-md hover:bg-white/5 sc-mono"
            >
              <n.icon className="w-3.5 h-3.5" /> {n.label}
              {n.label === "Browse Live Streams" && liveCount > 0 && (
                <span className="ml-1 inline-flex items-center gap-1 text-[9px] font-black text-[#FF3366] anim-pulse" data-testid="nav-live-count">
                  <span className="rec-dot" style={{ background: "#FF3366", width: 5, height: 5 }} />
                  {liveCount}
                </span>
              )}
            </Link>
          ))}
        </nav>

        <div className="flex items-center gap-2">
          {user && (
            <button
              type="button"
              data-testid="nav-friends"
              onClick={() => window.dispatchEvent(new Event("mysticast:toggle-friends"))}
              className="relative hidden sm:flex items-center justify-center w-9 h-9 rounded-md border border-white/10 hover:border-[color:var(--sc-pink)]/40 hover:bg-white/5 transition"
              title="Friends"
              aria-label="Open friends panel"
            >
              <Users className="w-4 h-4" />
              {social.requests.incoming.length > 0 && (
                <span className="absolute -top-1 -right-1 min-w-[16px] h-4 px-1 rounded-full text-[9px] font-bold flex items-center justify-center sc-mono" style={{ background: "var(--sc-pink)", color: "#fff" }} data-testid="nav-friends-badge">
                  {social.requests.incoming.length}
                </span>
              )}
            </button>
          )}
          {user && (
            <Link data-testid="nav-messages" to="/messages" className="relative hidden sm:flex items-center justify-center w-9 h-9 rounded-md border border-white/10 hover:border-[color:var(--sc-cyan)]/40 hover:bg-white/5 transition" title="Messages">
              <MessageSquare className="w-4 h-4" />
              {social.totalUnread > 0 && (
                <span className="absolute -top-1 -right-1 min-w-[16px] h-4 px-1 rounded-full text-[9px] font-bold flex items-center justify-center sc-mono" style={{ background: "var(--sc-pink)", color: "#fff" }}>
                  {social.totalUnread}
                </span>
              )}
            </Link>
          )}
          {user && (
            <Link data-testid="nav-credits" to="/credits" className="hidden sm:flex items-center gap-1.5 gold-pill hover:brightness-125 transition" title="Gold Coins — used to tip streamers (purchased)">
              <Coins className="w-4 h-4" />
              <span data-testid="navbar-gold-balance">{user.gold_coins ?? 0}</span>
            </Link>
          )}
          {user && (
            <Link data-testid="nav-sparks" to="/shop" className="hidden sm:flex items-center gap-1.5 sparks-pill hover:brightness-125 transition" title="Sparks — earned free, used for games + shop">
              <Sparkles className="w-3.5 h-3.5" />
              <span data-testid="navbar-sparks-balance">{user.sparks ?? user.credits ?? 0}</span>
            </Link>
          )}
          {!user ? (
            <>
              <Link data-testid={TIDS.navLogin} to="/login" className="sc-btn-ghost text-xs hidden sm:inline-flex items-center gap-1.5">
                <LogIn className="w-3.5 h-3.5" /> Log in
              </Link>
              <Link data-testid={TIDS.navSignup} to="/signup" className="sc-btn text-xs flex items-center gap-1.5">
                <UserPlus className="w-3.5 h-3.5" /> Sign up
              </Link>
            </>
          ) : (
            <>
              <Link data-testid={TIDS.navDashboard} to="/dashboard" className="sc-btn-ghost text-xs flex items-center gap-1.5 relative">
                <span className="relative inline-flex">
                  <LayoutDashboard className="w-3.5 h-3.5" />
                  <span className="absolute -bottom-0.5 -right-0.5 w-1.5 h-1.5 rounded-full bg-emerald-400 border border-black" title="Online" />
                </span>
                <span className="hidden sm:inline">{user.username}</span>
              </Link>
              <Link data-testid="nav-profile" to={`/u/${user.username}`} className="sc-btn-ghost text-xs flex items-center gap-1.5" title="Your profile">
                <span className="hidden sm:inline">Profile</span>
                <span className="sm:hidden">👤</span>
              </Link>
              <Link data-testid="nav-account" to="/account" className="sc-btn-ghost text-xs flex items-center gap-1.5" title="Account settings">⚙</Link>
              {(user.is_admin || user.is_demo) && (
                <Link data-testid="nav-admin" to="/admin" className="hidden sm:inline-flex items-center gap-1.5 px-2.5 py-1.5 rounded-md text-[10px] tracking-widest font-black uppercase sc-mono" style={{ background: "#FF1053", color: "#fff" }}>
                  Admin
                </Link>
              )}
              <button data-testid={TIDS.navLogout} onClick={() => { logout(); nav("/"); }} className="sc-btn-ghost text-xs">Logout</button>
            </>
          )}
        </div>
      </div>
    </header>
  );
}
